import { defineStore } from '@widgets/shared/utils/defineStore'
import type { WidgetAttachment, WidgetUploadedAttachment, WidgetUploadingAttachment } from '@widgets/shared/types'
import { computed, ref, shallowRef } from '@/composition/vue/compositionApi'
import type { ICancelablePromise } from '@/services/types'

type AttachmentUploadData = {
  file?: File
  promise?: ICancelablePromise<unknown>
}

export const useEditorAttachmentStore = defineStore(Symbol('editorAttachmentStore'), () => {
  const attachments = ref<WidgetAttachment[]>([])
  const attachmentUploadData = shallowRef<Record<string, AttachmentUploadData>>({})

  const attachmentIndexDataMap = computed(() =>
    attachments.value.reduce(
      (acc, attachment, index) => {
        acc[attachment.id] = {
          index,
          attachment,
        }
        return acc
      },
      {} as { [id: string]: { index: number; attachment: WidgetAttachment } },
    ),
  )

  const setAttachments = (newAttachments: WidgetAttachment[]) => {
    attachments.value = newAttachments
  }

  const clearAttachments = () => {
    attachments.value = []
    attachmentUploadData.value = {}
  }

  const replaceAttachment = (id: string, attachmentGetter: (foundAttachment: WidgetAttachment) => WidgetAttachment) => {
    const indexData = attachmentIndexDataMap.value[id]
    if (indexData) {
      attachments.value.splice(indexData.index, 1, attachmentGetter(indexData.attachment))
    }
  }

  const insertOrUpdateAttachmentUploadData = (id: string, uploadData: AttachmentUploadData) => {
    attachmentUploadData.value[id] = {
      ...(attachmentUploadData.value[id] ?? {}),
      ...uploadData,
    }
  }

  const insertOrUpdateUploadingAttachment = (
    attachment: WidgetUploadingAttachment,
    uploadData?: AttachmentUploadData,
  ) => {
    if (attachmentIndexDataMap.value[attachment.id]) {
      replaceAttachment(attachment.id, () => attachment)
    } else {
      attachments.value.push(attachment)
    }
    if (uploadData) {
      insertOrUpdateAttachmentUploadData(attachment.id, uploadData)
    }
  }

  const updateUploadingProgress = (id: string, progress: number) => {
    replaceAttachment(id, (attachment) => ({
      ...attachment,
      uploadProgress: Math.min(attachment.size, progress),
    }))
  }

  const markAttachmentAsUploaded = (id: string, uploadedAttachment: WidgetUploadedAttachment) => {
    replaceAttachment(id, () => uploadedAttachment)
    delete attachmentUploadData.value[id]
  }

  const setErrorForAttachment = (id: string, error: { text: string; canRetry: boolean }) => {
    replaceAttachment(id, (attachment) => ({
      ...attachment,
      error,
    }))
  }

  const getAttachmentUploadDataById = (id: string): AttachmentUploadData | undefined => {
    return attachmentUploadData.value[id]
  }

  const removeAttachment = (id: string) => {
    const indexData = attachmentIndexDataMap.value[id]
    if (indexData) {
      attachments.value.splice(indexData.index, 1)
    }
    const uploadData = attachmentUploadData.value[id]
    if (uploadData) {
      uploadData.promise?.cancel()
      delete attachmentUploadData.value[id]
    }
  }

  return {
    attachments,
    setAttachments,
    clearAttachments,
    insertOrUpdateUploadingAttachment,
    updateUploadingProgress,
    markAttachmentAsUploaded,
    setErrorForAttachment,
    removeAttachment,
    insertOrUpdateAttachmentUploadData,
    getAttachmentUploadDataById,
  }
})
